import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { 
  SpaceSchedule,
  SpaceScheduleResponse,
  BatchScheduleRequest,
  StandardWeekdayRequest,
  UpdateScheduleRequest,
  AvailabilityCheckResponse
} from '../models/sala.model';
import { environment } from '../../environments/environment';
import { friendlyError } from '../utils/friendly-error';
import { formatClockTime } from '../utils/format';

@Injectable({
  providedIn: 'root'
})
export class SpaceScheduleService {
  private readonly API_URL = `${environment.apiUrl}/space-schedule`;
  
  // Señal reactiva para los horarios
  public readonly schedules = signal<SpaceSchedule[]>([]);

  constructor(private http: HttpClient) {}

  /**
   * Obtener horarios de un espacio
   */
  getSchedulesBySpace(spaceId: number): Observable<SpaceScheduleResponse> {
    return this.http.get<SpaceScheduleResponse>(`${this.API_URL}/admin/space/${spaceId}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Verificar disponibilidad en fecha/hora específica
   */
  checkAvailability(spaceId: number, date: string, time: string): Observable<AvailabilityCheckResponse> {
    const params = new HttpParams()
      .set('date', date)
      .set('time', time);
    
    return this.http.get<AvailabilityCheckResponse>(`${this.API_URL}/space/${spaceId}/check`, { params }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Configurar horarios (batch)
   */
  setSchedules(spaceId: number, request: BatchScheduleRequest): Observable<SpaceScheduleResponse> {
    return this.http.post<SpaceScheduleResponse>(`${this.API_URL}/space/${spaceId}`, request).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Configurar horario estándar de lunes a viernes
   */
  setStandardWeekday(spaceId: number, request: StandardWeekdayRequest): Observable<SpaceScheduleResponse> {
    return this.http.post<SpaceScheduleResponse>(`${this.API_URL}/space/${spaceId}/standard-weekday`, request).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar horario específico
   */
  updateSchedule(scheduleId: number, request: UpdateScheduleRequest): Observable<SpaceSchedule> {
    return this.http.put<SpaceSchedule>(`${this.API_URL}/${scheduleId}`, request).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Eliminar horario
   */
  deleteSchedule(scheduleId: number): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/${scheduleId}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Cargar horarios en la señal reactiva
   */
  loadSchedules(spaceId: number): void {
    this.getSchedulesBySpace(spaceId).subscribe({
      next: (response) => {
        this.schedules.set(response.schedules);
      },
      error: (error) => {
        console.error('Error al cargar horarios:', error);
      }
    });
  }

  /**
   * Obtener nombre del día en español
   */
  getDayName(dayOfWeek: number): string {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return days[dayOfWeek] || '';
  }

  /**
   * Convertir hora de formato HH:MM:SS a HH:MM
   */
  formatTime(time: string): string {
    if (!time) return '';
    return time.substring(0, 5); // Toma solo HH:MM
  }

  /** Hora para textos de pantalla; no se usa como valor de formularios/API. */
  formatDisplayTime(time: string): string {
    return formatClockTime(time);
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const errorMessage = friendlyError(error, 'Ocurrió un error. Intenta nuevamente.');
    return throwError(() => new Error(errorMessage));
  }
}


